import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';

export interface BusinessPermissions {
  // Team Management
  canManageTeam: boolean;
  canInviteMembers: boolean;
  canRemoveMembers: boolean;
  canViewTeamAnalytics: boolean;
  canManageRoles: boolean;
  
  // Financial Management
  canManageTips: boolean;
  canViewRevenue: boolean;
  canConfigurePayments: boolean;
  canManageWallets: boolean;
  canExportFinancials: boolean;
  
  // Business Configuration
  canConfigureBusiness: boolean;
  canManageQRCodes: boolean;
  canConfigurePolicies: boolean;
  canManageIntegrations: boolean;
  canAccessAnalytics: boolean;
  
  // Content & Marketing
  canManageContent: boolean;
  canManageCampaigns: boolean;
  canAccessInsights: boolean;
  canManageBranding: boolean;
}

export interface BusinessAccess {
  isBusinessUser: boolean;
  isBusinessOwner: boolean;
  businessId: string | null;
  userLevel: string;
  permissions: BusinessPermissions;
  limits: {
    maxTeamMembers: number;
    maxQRCodes: number;
    maxMonthlyTransfers: number;
    canBypassBasicLimits: boolean;
  };
}

export function useBusinessPermissions(): BusinessAccess {
  const { user } = useAuth();

  const { data: businessData } = useQuery({
    queryKey: ['business-permissions', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      // Get user profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('user_level, ztartag')
        .eq('id', user.id)
        .single();

      if (!profile || !['business', 'enterprise'].includes(profile.user_level)) {
        return null;
      }

      // Get owned businesses - using existing table structure
      const { data: ownedBusinesses } = await supabase
        .from('businesses')
        .select('id, name, status')
        .eq('owner_id', user.id)
        .eq('status', 'active');

      // Get businesses where user is a member
      const { data: memberBusinesses } = await supabase
        .from('business_members')
        .select('business_id, role, approval_status')
        .eq('user_id', user.id)
        .eq('approval_status', 'approved');

      return {
        profile,
        ownedBusinesses: ownedBusinesses || [],
        memberBusinesses: memberBusinesses || []
      };
    },
    enabled: !!user?.id
  });

  if (!businessData || !businessData.profile) {
    return {
      isBusinessUser: false,
      isBusinessOwner: false,
      businessId: null,
      userLevel: 'freemium',
      permissions: {
        canManageTeam: false,
        canInviteMembers: false,
        canRemoveMembers: false,
        canViewTeamAnalytics: false,
        canManageRoles: false,
        canManageTips: false,
        canViewRevenue: false,
        canConfigurePayments: false,
        canManageWallets: false,
        canExportFinancials: false,
        canConfigureBusiness: false,
        canManageQRCodes: false,
        canConfigurePolicies: false,
        canManageIntegrations: false,
        canAccessAnalytics: false,
        canManageContent: false,
        canManageCampaigns: false,
        canAccessInsights: false,
        canManageBranding: false
      },
      limits: {
        maxTeamMembers: 0,
        maxQRCodes: 0,
        maxMonthlyTransfers: 0,
        canBypassBasicLimits: false
      }
    };
  }

  const { profile, ownedBusinesses, memberBusinesses } = businessData;
  const isBusinessOwner = ownedBusinesses.length > 0;
  const primaryBusinessId = isBusinessOwner ? ownedBusinesses[0].id : memberBusinesses[0]?.business_id || null;
  
  // Define permissions based on user level and ownership
  const isEnterprise = profile.user_level === 'enterprise';
  const isBusiness = profile.user_level === 'business';

  const permissions: BusinessPermissions = {
    // Team Management
    canManageTeam: isBusinessOwner,
    canInviteMembers: isBusinessOwner,
    canRemoveMembers: isBusinessOwner,
    canViewTeamAnalytics: isBusinessOwner || isEnterprise,
    canManageRoles: isBusinessOwner && isEnterprise,
    
    // Financial Management
    canManageTips: true,
    canViewRevenue: isBusinessOwner || isEnterprise,
    canConfigurePayments: isBusinessOwner,
    canManageWallets: true,
    canExportFinancials: isBusinessOwner || isEnterprise,
    
    // Business Configuration
    canConfigureBusiness: isBusinessOwner,
    canManageQRCodes: true,
    canConfigurePolicies: isBusinessOwner,
    canManageIntegrations: isBusinessOwner && isEnterprise,
    canAccessAnalytics: true,
    
    // Content & Marketing
    canManageContent: isBusinessOwner || isEnterprise,
    canManageCampaigns: isBusinessOwner && isEnterprise,
    canAccessInsights: isBusinessOwner || isEnterprise,
    canManageBranding: isBusinessOwner
  };

  const limits = {
    maxTeamMembers: isEnterprise ? 50 : isBusiness ? 5 : 0,
    maxQRCodes: isEnterprise ? 200 : isBusiness ? 50 : 0,
    maxMonthlyTransfers: isEnterprise ? 100000 : isBusiness ? 15000 : 0,
    canBypassBasicLimits: isEnterprise
  };

  return {
    isBusinessUser: true,
    isBusinessOwner,
    businessId: primaryBusinessId,
    userLevel: profile.user_level,
    permissions,
    limits
  };
}

// Hook to check specific business permission
export function useBusinessPermission(permission: keyof BusinessPermissions) {
  const { permissions } = useBusinessPermissions();
  return permissions[permission];
}

// Hook to check if user can access business admin dashboard
export function useCanAccessBusinessAdmin() {
  const { isBusinessUser } = useBusinessPermissions();
  return isBusinessUser;
}